// @flow
// Utils
import { formatCurrency } from '../commonHelpers';
// Types
import type { LineItem, LineItemVariant } from '../../current-order/types';
import type { GtmProduct } from '../types/GtmProduct';
import type { ReduxAction, ReduxActionCreator } from '../types/Redux';
import type { UserInformation } from '../../user/types';

type FormatLineItemVariantReturn = {
  isSoldByWeight: boolean,
  price: number,
  minimumQuantity: number,
  defaultQuantity: number,
};

export const actionMatches = (
  action: ReduxAction,
  actionCreator: ReduxActionCreator,
): string | boolean => {
  return !action.error && actionCreator.toString();
};

export const formatLineItemVariant = (
  variant: LineItemVariant,
): FormatLineItemVariantReturn => {
  const isSoldByWeight = variant.unitForQuantity === 'g';
  const price = isSoldByWeight
    ? Number(variant.price) * 1000
    : Number(variant.price);
  const minimumQuantity = isSoldByWeight
    ? variant.minimumQuantity / 1000
    : variant.minimumQuantity;
  const defaultQuantity = isSoldByWeight
    ? variant.defaultQuantity / 1000
    : variant.defaultQuantity;

  return { isSoldByWeight, price, minimumQuantity, defaultQuantity };
};

export const productToGtmProduct = (product: LineItemVariant): GtmProduct => {
  const { defaultQuantity, minimumQuantity, price } =
    formatLineItemVariant(product);

  return {
    item_id: product.slug,
    item_name: product.name,
    price: formatCurrency(price, 'number'),
    item_brand: product.manufacturer,
    ...(product.optionsText ? { item_variant: product.optionsText } : {}),
    quantity: 1,
    totalPrice: formatCurrency(price, 'number'),
    unitForQuantity: product.unitForQuantity,
    minimumQuantity,
    defaultQuantity,
  };
};

export const lineItemToGtmProduct = (lineItem: LineItem): GtmProduct => {
  const product = lineItem.variant;
  const subdepartments = product?.subdepartments ?? [];
  const isSoldByWeight = product.unitForQuantity === 'g';
  const quantity = isSoldByWeight
    ? lineItem.quantity / 1000
    : lineItem.quantity;
  const { defaultQuantity, minimumQuantity, price } =
    formatLineItemVariant(product);

  return {
    item_id: product.slug,
    item_name: product.name,
    price: formatCurrency(price, 'number'),
    item_brand: product.manufacturer,
    ...(product.optionsText ? { item_variant: product.optionsText } : {}),
    quantity,
    totalPrice: formatCurrency(product.price * lineItem.quantity, 'number'),
    unitForQuantity: product.unitForQuantity,
    minimumQuantity,
    defaultQuantity,
    ...subdepartments.reduce(
      (acc, subdepartment, index) => ({
        ...acc,
        [`item_category${index === 0 ? '' : index + 1}`]: subdepartment.name,
      }),
      {},
    ),
  };
};

export const gtmItemsValue = (items: GtmProduct[]) => {
  const value = items.reduce(
    (total, item) => total + Number(item.price) * item.quantity,
    0,
  );

  return formatCurrency(value, 'number');
};

// GA4 recommends clearing the previous ecommerce object before pushing a new
// one, so unrelated properties from a prior event don't leak into this hit.
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
export const clearEcommerce = () => ({ ecommerce: null });

export const getCurrentUser = (user: UserInformation) => {
  return {
    external_id: user.id?.toString(),
    email_address: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    city: 'Salvador',
    country: 'BR',
  };
};
